<template>
  <span
    v-if="visible"
    class="license-badge"
    :class="badgeClass"
    :title="tooltipText"
    @click="handleClick"
  >
    {{ badgeText }}
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuth } from '../composables/useAuth';
import { useEntitlement } from '../composables/useEntitlement';
import { useDialogStore } from '../stores/dialogs';

const auth = useAuth();
const entitlement = useEntitlement();
const dialogStore = useDialogStore();

const MAX_EXTENSIONS = 3;
const GRACE_YELLOW_THRESHOLD = 4;

const visible = computed((): boolean => {
  return auth.isLocalMode.value && entitlement.effectiveExpiresAt.value !== null;
});

const effectiveDays = computed((): number | null => {
  return entitlement.effectiveDaysUntilExpiry.value;
});

const isExpired = computed((): boolean => {
  return effectiveDays.value !== null && effectiveDays.value < 0;
});

const canExtend = computed((): boolean => {
  return isExpired.value && entitlement.graceCount.value < MAX_EXTENSIONS;
});

type BadgeState = 'green' | 'yellow' | 'red' | 'expired';

const badgeState = computed((): BadgeState => {
  const days = effectiveDays.value;
  if (days === null) return 'green';

  if (days < 0) return 'expired';

  if (entitlement.usingGracePeriod.value) {
    return days > GRACE_YELLOW_THRESHOLD ? 'yellow' : 'red';
  }

  if (days > 30) return 'green';
  if (days >= 14) return 'yellow';
  return 'red';
});

const badgeClass = computed(() => ({
  'badge-green': badgeState.value === 'green',
  'badge-yellow': badgeState.value === 'yellow',
  'badge-red': badgeState.value === 'red',
  'badge-expired': badgeState.value === 'expired',
  'badge-clickable': canExtend.value,
}));

const badgeText = computed((): string => {
  const days = effectiveDays.value;
  if (days === null) return '';

  if (days < 0) return 'Expired';

  if (entitlement.usingGracePeriod.value) {
    return `Grace: ${days}d left`;
  }

  if (days > 30) {
    const date = entitlement.effectiveExpiryDate.value;
    if (!date) return '';
    return `Expires ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }

  return `Expires in ${days}d`;
});

const tooltipText = computed((): string => {
  const date = entitlement.effectiveExpiryDate.value;
  if (!date) return '';

  const dateStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  if (isExpired.value) {
    const remaining = MAX_EXTENSIONS - entitlement.graceCount.value;
    if (remaining > 0) {
      return `License expired. Click for a 7-day extension (${remaining} remaining)`;
    }
    return 'License expired. No extensions remaining. Please renew.';
  }

  if (entitlement.usingGracePeriod.value) {
    return `Grace period until ${dateStr} (${entitlement.graceCount.value}/${MAX_EXTENSIONS} extensions used)`;
  }

  return `License valid until ${dateStr}`;
});

async function handleClick(): Promise<void> {
  if (!canExtend.value) return;

  const remaining = MAX_EXTENSIONS - entitlement.graceCount.value;
  const confirmed = await dialogStore.confirm(
    'Your license has expired. You can request a temporary 7-day extension ' +
    'to keep using MasoDJ while you renew your license.\n\n' +
    'Please contact support or renew your subscription as soon as possible.\n\n' +
    `Extensions used: ${entitlement.graceCount.value}/${MAX_EXTENSIONS} ` +
    `(${remaining} remaining)`,
    { confirmText: 'Extend 7 Days', cancelText: 'Not Now' }
  );

  if (!confirmed) return;

  const result = await entitlement.requestExtension();
  if (result.success) {
    await dialogStore.alert(
      'Extension granted! You have 7 more days.\n\n' +
      `Extensions used: ${result.graceCount}/${MAX_EXTENSIONS}`,
      { confirmText: 'OK' }
    );
  } else {
    await dialogStore.alert(
      result.error || 'Failed to request extension.',
      { confirmText: 'OK' }
    );
  }
}
</script>

<style scoped>
.license-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  user-select: none;
  letter-spacing: 0.02em;
}

.badge-clickable {
  cursor: pointer;
}

.badge-clickable:hover {
  filter: brightness(1.15);
}

.badge-green {
  background-color: #16a34a;
  color: #fff;
}

.badge-yellow {
  background-color: #d97706;
  color: #fff;
}

.badge-red {
  background-color: #dc2626;
  color: #fff;
}

.badge-expired {
  background-color: #7f1d1d;
  color: #fff;
  animation: pulse-badge 1.5s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
